<template>
  <div class="dashboard_page_container">
    <div class="page-title-row">
      <h2 style="margin-bottom: 0;">Mis Planes</h2>
      <button @click="createNewPlan" class="main_button-green">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
        </svg>
        Nuevo plan
      </button>
    </div>

    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th class="big">Plan</th>
            <th class="small">Estado</th>
            <th class="small">Fecha fin</th>
            <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in plans"
            :key="item.codigo"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'" >
            <td style="font-weight: 600;">{{ item.name }}</td>
            <td>
              <span 
                class="badge" 
                :class="{
                  'badge-success': item.type === 'promocion' && item.status === 'Activo',
                  'badge-warning': item.type === 'draft',
                  'badge-neutral': item.status === 'Caducado'
                }"
              >
                {{ item.status || (item.type === 'draft' ? 'Borrador' : 'Publicado') }}
              </span>
            </td>
            <td>{{ item.expiration_date !== null ? formatDate(item.expiration_date) : 'Sin definir' }}</td>
            <td class="clickable">
              <img src="@/assets/images/ic_eye.png" alt="Ver" class="action_icon" @click="planClicked(item)">
              <img v-if="item.type === 'promocion'" src="@/assets/images/ic_edit.png" alt="Editar" class="action_icon" @click="editPlazasClicked(item)">
            </td>
          </tr>
          <tr v-if="plans.length === 0 && !loadingStore.isLoading">
            <td colspan="4" style="padding: 2.5rem; text-align: center; color: var(--text-color-light);">
              No tienes planes creados aún.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <TwoButtonPopup :title="twoButtonTitle" :message="twoButtonMessage" :acceptButton="twoButtonAcceptButton" @acceptCallback="onAcceptCallback" :cancelButton="twoButtonCancelButton" @cancelCallback="onCancelCallback"/>
    <DraftPlanPopup :plan="draftPlan" @close="draftPlan = null" @saved="error = $event" @error="error = $event" />
    <PlanDetailsPopup :plan="planDetails" @close="planDetails = null" @saved="error = $event" @error="error = $event" @add-new-date="addNewDate" @ask-for-confirmation="askForConfirmation" @add-new-product="createNewProduct" />
    <PlanDetailsProductNewDatePopup :product="productToClone" @close-add-date="productToClone = null" @saved-add-date="getPlanDetails($event)" @error-add-date="error = $event" />
    <CreateNewProductPopup :plan="planToCreateNewProduct" @close="planToCreateNewProduct = null" @saved="getPlanDetails($event)" @error="error = $event" />
    <ErrorPopup :errorMessage="error" @closed="error = null" />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { refreshToken } from "@/services/authService";

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import DraftPlanPopup from "./common/DraftPlanPopup.vue";
import PlanDetailsPopup from "./common/PlanDetailsPopup.vue";
import PlanDetailsProductNewDatePopup from "./common/PlanDetailsProductNewDatePopup.vue";
import TwoButtonPopup from "./common/TwoButtonPopup.vue";
import CreateNewProductPopup from "./common/CreateNewProductPopup.vue";

const error = ref(null);
const plans = ref([]);
const loadingStore = useLoadingStore();
const navigationStore = useNavigationStore();
const router = useRouter();
const draftPlan = ref(null);
const planDetails = ref(null);
const productToClone = ref(null);
const planToCreateNewProduct = ref(null);

const twoButtonTitle = ref("");
const twoButtonMessage = ref("");
const twoButtonAcceptButton = ref("");
const twoButtonCancelButton = ref("");
const onAcceptCallback = ref(null);
const onCancelCallback = ref(null);

const getPlans = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchPlans();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchPlans();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo tus planes.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      plans.value = [];
    } else {
      error.value = null;
      plans.value = data.datos;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
};

navigationStore.setNavigationPosition(3);
getPlans();

async function fetchPlans() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/plans/get_my_plans?user_id="+localStorage.getItem('user_id'), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      }
    });
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function planClicked(plan) {
  if (plan.type == "draft") {
    getDraftPlanDetails(plan.id)
  } else {
    window.open(plan.url, '_blank');
  }  
}

function editPlazasClicked(plan) {
    getPlanDetails(plan.id)
}

function createNewPlan() {
  draftPlan.value = {
    "id": 0,
    "name": "",
    "description": "",
    "date": null,
    "hour": null,
    "capacity": 0,
    "price": 0,
    "expiration_date": null,
    "images": []
  };
}

async function getDraftPlanDetails(planId) {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchDraftPlanDetails(planId);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchDraftPlanDetails(planId);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo los detalles de tu plan.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      draftPlan.value = null;
    } else {
      error.value = null;
      draftPlan.value = data.datos;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchDraftPlanDetails(planId) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/plans/get_draft_plan_details?user_id="+localStorage.getItem('user_id')+"&plan_id="+planId, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      }
    });
}

async function getPlanDetails(planId) {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchPlanDetails(planId);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchPlanDetails(planId);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo los detalles de tu plan.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      planDetails.value = null;
    } else {
      error.value = null;
      planDetails.value = data.promo;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchPlanDetails(planId) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/plans/get_details?user_id="+localStorage.getItem('user_id')+"&promo_id="+planId, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      }
    });
}

const addNewDate = (product) => {
  productToClone.value = product;
};

const createNewProduct = (plan) => {
  const planData = plans.value.find(p => p.id === plan.id_promo);
  if (planData.status === "Caducado") {
    error.value = "El plan está caducado, no se puede crear un nuevo producto.";
    return;
  }

  planToCreateNewProduct.value = plan;
};

const askForConfirmation = (message, onConfirmCallback) => {
  twoButtonTitle.value = "Confirmación";
  twoButtonMessage.value = message;
  twoButtonAcceptButton.value = "Sí";
  twoButtonCancelButton.value = "No";
  onAcceptCallback.value = () => {
    onConfirmCallback();
    twoButtonTitle.value = "";
    twoButtonMessage.value = "";
    twoButtonAcceptButton.value = "";
    twoButtonCancelButton.value = "";
  };
  onCancelCallback.value = () => {
    twoButtonTitle.value = "";
    twoButtonMessage.value = "";
    twoButtonAcceptButton.value = "";
    twoButtonCancelButton.value = "";
  };
};
</script>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: var(--gray);
  color: white;
  border: 0px;
}

.big {
  width: 55%;
}

.small {
  width: 15%;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

</style>
